import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import translations from '../i18n/translations';
import { useAuth } from './AuthContext';

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const { user, updateLanguage } = useAuth();
  const [language, setLanguageState] = useState(() => {
    // Get from localStorage or user preference or default to 'tr' (Turkish)
    const stored = localStorage.getItem('language');
    return stored || 'tr';
  });

  useEffect(() => {
    // Sync with user preference when user loads
    if (user?.language && user.language !== language) {
      setLanguageState(user.language);
      localStorage.setItem('language', user.language);
    }
  }, [user, language]);

  const setLanguage = useCallback(async (lang) => {
    if (lang !== 'en' && lang !== 'tr') return;
    setLanguageState(lang);
    localStorage.setItem('language', lang);
    
    // Update on server if authenticated
    if (user) {
      await updateLanguage(lang);
    }
  }, [user, updateLanguage]);

  const t = useCallback((key) => {
    return translations[language]?.[key] || translations['en']?.[key] || key;
  }, [language]);

  const value = {
    language,
    setLanguage,
    t,
    isEnglish: language === 'en',
    isTurkish: language === 'tr'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
